import React from "react";
import {
  Box,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { getFormSectionStyles } from "./formSectionStyles";

type WizardModule = { id: number; name: string };

interface WizardModuleSelectorProps {
  /** enabled / disabled as stored on the config: a map keyed "0","1",... (arrays also accepted). */
  enabled: Record<string, WizardModule> | WizardModule[] | undefined;
  disabled: Record<string, WizardModule> | WizardModule[] | undefined;
  /** Module names to hide entirely (e.g. External/Pano on mobile units). */
  hiddenModules?: string[];
  onChange: (
    enabled: Record<number, WizardModule>,
    disabled: Record<number, WizardModule>,
  ) => void;
}

const toArray = (v: any): WizardModule[] => (v ? Object.values(v) : []);

const toKeyedMap = (arr: WizardModule[]): Record<number, WizardModule> =>
  arr.reduce((acc: Record<number, WizardModule>, item, index) => {
    acc[index] = item;
    return acc;
  }, {});

// "WizardVinConfirmation" -> "Vin Confirmation"
const humanize = (name: string) =>
  name
    .replace(/^Wizard/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim() || name;

const WizardModuleSelector: React.FC<WizardModuleSelectorProps> = ({
  enabled,
  disabled,
  hiddenModules = [],
  onChange,
}) => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);

  const hidden = new Set(hiddenModules);
  const enabledList = toArray(enabled).filter((m) => !hidden.has(m.name));
  const disabledList = toArray(disabled).filter((m) => !hidden.has(m.name));

  // Hidden modules are dropped from both lists — never re-persisted for units
  // that shouldn't offer them.
  const emit = (nextEnabled: WizardModule[], nextDisabled: WizardModule[]) =>
    onChange(toKeyedMap(nextEnabled), toKeyedMap(nextDisabled));

  const enable = (name: string) => {
    const item = disabledList.find((m) => m.name === name);
    if (!item) return;
    emit([...enabledList, item], disabledList.filter((m) => m.name !== name));
  };

  const disable = (name: string) => {
    const item = enabledList.find((m) => m.name === name);
    if (!item) return;
    emit(enabledList.filter((m) => m.name !== name), [...disabledList, item]);
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= enabledList.length) return;
    const next = [...enabledList];
    [next[index], next[target]] = [next[target], next[index]];
    emit(next, disabledList);
  };

  const listSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    py: 0,
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2.5,
      }}
    >
      {/* Enabled */}
      <Box>
        <Typography sx={styles.eyebrow}>Enabled Steps</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          Run in this order. Uncheck to disable; use the arrows to reorder.
        </Typography>
        <List dense sx={listSx}>
          {enabledList.length === 0 && (
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                primary="No steps enabled."
              />
            </ListItem>
          )}
          {enabledList.map((m, index) => (
            <ListItem
              key={m.name}
              disablePadding
              secondaryAction={
                <Stack direction="row" spacing={0.25}>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        disabled={index === enabledList.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }
            >
              <ListItemButton onClick={() => disable(m.name)} data-cy={`wizard-enabled-${m.name}`}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <Checkbox edge="start" checked tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText primary={`${index + 1}. ${humanize(m.name)}`} secondary={m.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Disabled */}
      <Box>
        <Typography sx={styles.eyebrow}>Disabled Steps</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          Check to enable (added to the end of the enabled list).
        </Typography>
        <List dense sx={listSx}>
          {disabledList.length === 0 && (
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
                primary="All steps enabled."
              />
            </ListItem>
          )}
          {disabledList.map((m) => (
            <ListItem key={m.name} disablePadding>
              <ListItemButton onClick={() => enable(m.name)} data-cy={`wizard-disabled-${m.name}`}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <Checkbox edge="start" checked={false} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText primary={humanize(m.name)} secondary={m.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default WizardModuleSelector;
